import Tooltip from 'antd/lib/tooltip';
import React from 'react';
import { useSelector } from 'react-redux';
import { displayTextForUserGroup, displayUserNameLong } from '../../helpers/displayHelpers';
import { RootState } from '../../reducers/reducers';
import { selectReleasePilotForRelease } from '../../selectors/selectors';
import { DefaultUserGroupId, UserGroup } from '../../types';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import { antGrey } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { selectOrgUsersWithGroup } from '../../selectors/memoizedSelectors';

interface GroupTooltipProps extends ReleaseComponentProps {
    group: UserGroup;
    showComma: boolean;
    isDisabled: boolean;
}

export const useGroupTooltipContent = (
    group: UserGroup | undefined,
    appId: string,
    releaseId: string,
): React.ReactNode => {
    const usersWithGroup = useSelector((state: RootState) => selectOrgUsersWithGroup(state, group?.id ?? ''));
    const releasePilot = useSelector((state: RootState) => selectReleasePilotForRelease(state, appId, releaseId));

    if (!group) {
        return null;
    }

    if (group.id === DefaultUserGroupId.Pilot) {
        return releasePilot ?
                `${displayUserNameLong(releasePilot)} is the release pilot`
            :   `This release has no release pilot`;
    }
    if (usersWithGroup.length === 0) {
        return `No users within user group`;
    }
    return (
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {usersWithGroup.map((user, index) => (
                <div key={index}>{displayUserNameLong(user)}</div>
            ))}
        </div>
    );
};

const GroupTooltip: React.FC<GroupTooltipProps> = (props: GroupTooltipProps) => {
    const tooltipContent = useGroupTooltipContent(props.group, props.appId, props.releaseId);

    if (!props.group) {
        return <></>;
    }

    const displayText = (
        <Text style={{ color: props.isDisabled ? antGrey.grey6 : antGrey.grey8 }}>
            {displayTextForUserGroup(props.group)}
            {props.showComma && ', '}
        </Text>
    );
    return (
        <>
            {props.isDisabled ?
                <>{displayText}</>
            :   <Tooltip title={tooltipContent} placement="topRight">
                    <>{displayText}</>
                </Tooltip>
            }
        </>
    );
};

export default GroupTooltip;
